import { createContext, useContext } from "react";
import { makeAutoObservable, runInAction } from "mobx";
import { Account, Coordinate, Session } from "@root/utils/entities";
import { AuthModalStore } from "./authModalStore";
import { Timeout } from "@root/utils/enums";
import { navigate } from "vike/client/router";
import { isBrowser } from "@root/utils/functions";
import { ModalStore } from "./modalStore";
import { subscriptionService } from "@root/services/api/subscriptionService";
import {
  Feature,
  UserSubscription,
  OrganizationSubscription,
} from "@root/components/subscription/types";
import {
  setSecureItem,
  getSecureItem,
  removeSecureItem,
} from "@root/utils/secureStorage";
import { accountService } from "@root/services/api/accountService";

const SESSION_DATA_NAME = "EP-Session";
const SUBSCRIPTION_FEATURES_NAME = "EP-SubscriptionFeatures";

// PageLayout builds a new AccountStore on every client-side navigation, and each
// one used to re-fetch `/current` (one call per page change). The page-load
// re-sync now runs at most once per active profile per FEATURE_RESYNC_MS, and
// concurrent ones share a request. Module state resets on full reload, which
// profile switching always does, so a switched profile always re-syncs.
// Explicit loadAndStoreSubscriptionFeatures() calls (login, switch, checkout,
// cancel) are not throttled.
const FEATURE_RESYNC_MS = 60_000;
let lastFeatureSync: { contextId: string; at: number } | undefined;
let featureSyncInFlight: string | undefined;

export class AccountStore {
  readonly authModalStore = new AuthModalStore();
  readonly switchProfileModalStore = new ModalStore();

  account?: Account;
  session?: Session;
  sessionVersion: number = 0; // Increment to force re-render
  unverifiedEmail?: string;
  isAuthenticated: boolean = false;
  menuExpanded: boolean = false;
  locationRequested: boolean = false;
  errorMessage?: string;
  successMessage?: string;
  userLocation?: Coordinate;
  isLoadingSubscriptionFeatures: boolean = false;
  hasAttemptedLoadSubscriptionFeatures: boolean = false;

  constructor(account?: Account) {
    makeAutoObservable(this);
    this.account = account;

    if (this.account) {
      this.session = this.getAccountSession();

      if (!this.session) {
        const session = new Session(
          this.account.id,
          this.displayName,
          "user",
          this.account.role,
          this.account.imageUrl,
          undefined, // planName
          undefined, // planColor
          this.account.maxDate,
        );
        this.setAccountSession(session);
        this.session = session;
      }

      // Re-sync cached subscription features with the active profile on page
      // load. Without this, a failed/raced background load after a profile
      // switch leaves the previous profile's features cached indefinitely
      // (e.g. an org admin stuck with their personal Free-plan features).
      this.resyncSubscriptionFeaturesOnLoad();
    }
  }

  private resyncSubscriptionFeaturesOnLoad() {
    if (!isBrowser()) return;
    const contextId = this.getAccountSession()?.id;
    if (!contextId) return;

    const recentlySynced =
      lastFeatureSync?.contextId === contextId &&
      Date.now() - lastFeatureSync.at < FEATURE_RESYNC_MS;
    if (recentlySynced || featureSyncInFlight === contextId) {
      // Cache already matches this profile; count it as loaded for this store
      this.hasAttemptedLoadSubscriptionFeatures = true;
      return;
    }

    featureSyncInFlight = contextId;
    this.loadAndStoreSubscriptionFeatures().finally(() => {
      if (featureSyncInFlight === contextId) featureSyncInFlight = undefined;
    });
  }

  get displayName(): string {
    return `${this.account?.firstName} ${this.account?.lastName}`;
  }

  get profiles(): Session[] {
    if (!this.account) return [];

    return (
      this.account.profiles?.map((p) =>
        p instanceof Session ? p : Session.fromJSON(p),
      ) ?? [
        new Session(
          this.account!.id,
          this.displayName,
          "user",
          this.account.role,
          this.account.imageUrl,
          undefined, // planName
          undefined, // planColor
          this.account.maxDate,
        ),
      ]
    );
  }

  get isOrganizationProfile() {
    return this.session?.type === "organization";
  }

  get isUserProfile() {
    return this.session?.type === "user";
  }

  setAccount(account: Account) {
    this.account = account;
    this.isAuthenticated = true;
    this.errorMessage = undefined;
    this.unverifiedEmail = undefined;

    // Update session with role and imageUrl from account
    if (this.account) {
      const session = new Session(
        this.account.id,
        this.displayName,
        "user",
        this.account.role, // Pass the role to session
        this.account.imageUrl, // Pass the imageUrl to session
        undefined, // planName - account might not have it directly if it's separate
        undefined, // planColor
        this.account.maxDate,
      );
      this.setAccountSession(session);
      runInAction(() => {
        this.session = session;
        this.sessionVersion += 1;
      });
    }
  }

  async login(account: Account) {
    this.setAccount(account);

    // Load and store subscription features on login
    // This will only update if there's a subscription, preserving existing features on errors
    await this.loadAndStoreSubscriptionFeatures();

    if (this.account?.emailConfirmed) {
      if (this.authModalStore.redirectUrl) {
        await navigate(this.authModalStore.redirectUrl);
      } else {
        window.location.reload();
      }
      this.authModalStore.closeAuthModal();
    } else {
      this.unverifiedEmail = account.email;
      this.authModalStore.changeAuthModalType("verification");
    }
  }

  async refreshAccount() {
    try {
      const response = await accountService.getAccount();
      if (response.data) {
        runInAction(() => {
          this.account = response.data;
          this.sessionVersion += 1;
        });
      }
    } catch {
      console.error("Failed to refresh account");
    }
  }

  async loadAndStoreSubscriptionFeatures(
    subscriptionData?: UserSubscription | OrganizationSubscription | null,
  ) {
    if (!isBrowser() || !this.account?.id) {
      // Only set to null if we're sure there's no account
      // Don't clear existing features on browser/account check failures
      return;
    }

    runInAction(() => {
      this.isLoadingSubscriptionFeatures = true;
      this.hasAttemptedLoadSubscriptionFeatures = true;
    });

    try {
      // If subscription data is explicitly provided (including null), use it
      // null means no subscription, undefined means fetch from API
      if (subscriptionData !== undefined) {
        // Explicit subscription data provided (could be null or a subscription object)
        if (subscriptionData === null) {
          // Explicitly no subscription - set to null
          this.setSubscriptionFeatures(null);
          return;
        }

        // Subscription object provided - extract features
        // Check if it's an enhanced subscription type with features property
        const subscriptionAny = subscriptionData as any;
        if (
          subscriptionAny.features &&
          Array.isArray(subscriptionAny.features)
        ) {
          // Enhanced subscription with features array - convert to Feature[] format
          const features: Feature[] = subscriptionAny.features.map(
            (f: any) => ({
              id: f.id,
              name: f.name,
              description: f.description || "",
              valueNeeded: f.value !== null && f.value !== undefined,
            }),
          );
          this.setSubscriptionFeatures(features);
        } else {
          // Subscription exists but has no features property (base subscription type)
          // This means we don't have feature data - set to null (no plan features)
          this.setSubscriptionFeatures(null);
        }
        return;
      }

      // If subscription data is not provided (undefined), fetch it from API
      // Check current session type to determine which subscription to load
      const currentSession = this.getAccountSession();
      const isOrganizationContext = currentSession?.type === "organization";
      const contextId = currentSession?.id;

      if (!contextId) {
        // No context ID means we can't determine subscription status
        // Don't clear existing features - preserve them
        return;
      }

      // Use new enhanced endpoint for users (includes features), old endpoint for organizations
      const response = isOrganizationContext
        ? await subscriptionService.getCurrentByOrganizationId(contextId)
        : await subscriptionService.getCurrentByUserIdWithDetails(contextId);

      const subscription = response?.data;
      // Throttle the page-load re-sync only after a definitive answer; a
      // transport error (errors[] set) lets the next page load retry
      if (response && !(response as { errors?: unknown[] }).errors?.length) {
        lastFeatureSync = { contextId, at: Date.now() };
      }

      // Only update features if we have definitive subscription data
      // Set to null only when we're certain there's no subscription
      if (subscription) {
        // Check if features are directly in the response (enhanced subscription type)
        const subscriptionAny = subscription as any;
        if (
          subscriptionAny.features &&
          Array.isArray(subscriptionAny.features)
        ) {
          // Enhanced subscription with features array - convert to Feature[] format
          const features: Feature[] = subscriptionAny.features.map(
            (f: any) => ({
              id: f.id,
              name: f.name,
              description: f.description || "",
              valueNeeded: f.value !== null && f.value !== undefined,
            }),
          );
          this.setSubscriptionFeatures(features);
        } else {
          // Subscription exists but has no features property (base subscription type)
          // This means we don't have feature data - set to null (no plan features)
          this.setSubscriptionFeatures(null);
        }
      } else {
        // No subscription found - set to null
        this.setSubscriptionFeatures(null);
      }
    } catch (error) {
      console.error("Error loading subscription features:", error);
      // On error, preserve existing features - don't clear them
      // Only set to null when we're certain there's no subscription
      // This ensures features persist through temporary API errors
    } finally {
      runInAction(() => {
        this.isLoadingSubscriptionFeatures = false;
      });
    }
  }

  setSubscriptionFeatures(features: Feature[] | null) {
    if (!isBrowser()) return;

    // Store sensitive subscription features with base64 encoding
    setSecureItem(SUBSCRIPTION_FEATURES_NAME, features);
  }

  getSubscriptionFeatures(): Feature[] | null {
    if (!isBrowser()) return null;

    // Retrieve and decrypt subscription features from secure storage
    return getSecureItem<Feature[] | null>(SUBSCRIPTION_FEATURES_NAME);
  }

  get isAdmin(): boolean {
    return this.session?.role === "Admin";
  }

  hasFeature(featureId: number): boolean {
    const features = this.getSubscriptionFeatures();
    if (!features || !Array.isArray(features)) {
      return false;
    }

    return features.some((feature) => feature.id === featureId);
  }

  authenticate(email: string) {
    this.isAuthenticated = true;
    this.unverifiedEmail = email;
    this.authModalStore.changeAuthModalType("verification");
  }

  setUnverifiedEmail(email: string) {
    this.unverifiedEmail = email;
    this.isAuthenticated = false;
    this.authModalStore.changeAuthModalType("verification");
  }

  setPendingEmail(email: string) {
    this.unverifiedEmail = email;
    this.authModalStore.changeAuthModalType("verification");
  }

  updateAccountInfo(firstName: string, lastName: string) {
    this.errorMessage = undefined;

    if (this.account) {
      this.account.firstName = firstName;
      this.account.lastName = lastName;
    }
  }

  updateAccountImage(imageUrl?: string) {
    runInAction(() => {
      this.errorMessage = undefined;

      if (this.account) {
        this.account.imageUrl = imageUrl;

        // Update session imageUrl if it's a user session or matches account id
        if (
          this.session &&
          (this.session.type === "user" || this.session.id === this.account.id)
        ) {
          this.session.imageUrl = imageUrl;
          this.setAccountSession(this.session);
          this.sessionVersion += 1;
        }
      }
    });
  }

  updateAccountEmail(email: string) {
    this.errorMessage = undefined;

    if (this.account) {
      this.account.email = email;
    }
  }

  setErrorMessage(message: string) {
    this.errorMessage = message;

    setTimeout(() => {
      runInAction(() => {
        this.clearErrorMessage();
      });
    }, Timeout.SuccessMessage);
  }

  clearErrorMessage() {
    this.errorMessage = undefined;
  }

  setSuccessMessage(message: string) {
    this.successMessage = message;

    setTimeout(() => {
      runInAction(() => {
        this.clearSuccessMessage();
      });
    }, Timeout.SuccessMessage);
  }

  clearSuccessMessage() {
    this.successMessage = undefined;
  }

  toggleMenu() {
    this.menuExpanded = !this.menuExpanded;
  }

  setUserLocation(lat: number, lng: number) {
    this.userLocation = {
      latitude: lat.toString(),
      longitude: lng.toString(),
    };
    this.locationRequested = true;
  }

  clearUserLocation() {
    this.userLocation = undefined;
    this.locationRequested = true;
  }

  setAccountSession(session: Session | any) {
    if (!isBrowser() || !session) return;

    // If it's already a Session instance, use toJSON, otherwise assume it's a plain object
    const json =
      typeof session.toJSON === "function" ? session.toJSON() : session;

    // Store sensitive session data with base64 encoding
    setSecureItem(SESSION_DATA_NAME, json);
  }

  getAccountSession(): Session | undefined {
    if (isBrowser()) {
      // Retrieve and decrypt session data from secure storage
      const sessionObj = getSecureItem<any>(SESSION_DATA_NAME);
      if (sessionObj) {
        // For user sessions, ensure imageUrl matches account if account has it
        let imageUrl = sessionObj.imageUrl;
        if (sessionObj.type === "user" && this.account?.imageUrl) {
          // Use account's imageUrl if it exists (it's the source of truth)
          imageUrl = this.account.imageUrl;
        }

        // Create a new Session instance from the stored data
        const session = new Session(
          sessionObj.id,
          sessionObj.name,
          sessionObj.type,
          sessionObj.role,
          imageUrl,
          sessionObj.planName,
          sessionObj.planColor,
          sessionObj.maxDate || this.account?.maxDate,
        );
        // Ensure role is preserved when getting session
        if (this.account?.role && !session.role) {
          session.role = this.account.role;
          this.setAccountSession(session);
        }
        // If imageUrl was updated, save it
        if (imageUrl !== sessionObj.imageUrl) {
          this.setAccountSession(session);
        }
        return session;
      }
    }
    return undefined;
  }

  clearAccountSession() {
    if (!isBrowser()) return;

    // Clear all sensitive data from secure storage on logout
    removeSecureItem(SESSION_DATA_NAME);
    removeSecureItem(SUBSCRIPTION_FEATURES_NAME);
  }

  showSwitchProfileModal() {
    this.switchProfileModalStore.open();
  }

  refreshSession() {
    if (this.account) {
      const storedSession = this.getAccountSession();
      if (storedSession) {
        // getAccountSession() already handles imageUrl syncing
        runInAction(() => {
          this.session = storedSession;
          this.sessionVersion += 1;
        });
      }
    }
  }

  async switchProfile(session: Session) {
    // Backend now includes imageUrl in profiles, no need to fetch
    const newSession = new Session(
      session.id,
      session.name,
      session.type,
      session.role,
      session.imageUrl,
      session.planName,
      session.planColor,
      session.maxDate,
    );
    this.setAccountSession(newSession);
    runInAction(() => {
      this.session = newSession;
      this.sessionVersion += 1;
    });
    this.switchProfileModalStore.close();
    // Await so gated UI reached right after switching reads the new
    // profile's features instead of the previous profile's cached ones
    await this.loadAndStoreSubscriptionFeatures();
  }

  async switchToOrgProfile(id: string) {
    const profile = this.profiles.find((p) => p.id === id);

    if (profile) {
      // Backend now includes imageUrl in profiles, no need to fetch
      const newSession = new Session(
        profile.id,
        profile.name,
        profile.type,
        profile.role,
        profile.imageUrl,
        profile.planName,
        profile.planColor,
        profile.maxDate,
      );
      this.setAccountSession(newSession);
      runInAction(() => {
        this.session = newSession;
        this.sessionVersion += 1;
      });
      // Await so gated UI reached right after switching reads the new
      // profile's features instead of the previous profile's cached ones
      await this.loadAndStoreSubscriptionFeatures();
    }
  }
}

export const AccountContext = createContext<AccountStore>(null!);
export const useAccountContext = () => useContext(AccountContext);
